import Link from "next/link";
import CardImage from "@/components/CardImage";
import { articleImageUrl } from "@/lib/blogTopics";

export interface ArticleCardData {
  _id?: string;
  slug: string;
  title: string;
  topic: string;
  /** Display label resolved server-side. Falls back to slug if missing. */
  topicLabel?: string;
  coverImage?: string;
  excerpt?: string;
  publishedAt?: string | Date;
}

const fmtDate = (d?: string | Date) =>
  d
    ? new Date(d).toLocaleDateString("en-SG", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

/**
 * Card variant shared by /blog index and the bottom-of-article
 * "Continue Reading" rail. Mirrors PropertyCard's layout language
 * (image top, content below, light shadow + hover lift) so the two
 * grids feel cohesive when they appear on the same page.
 */
export default function ArticleCard({ article }: { article: ArticleCardData }) {
  return (
    <Link
      href={`/blog/${article.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-[0px_4px_4px_rgba(0,0,0,0.04)] transition hover:shadow-[0px_8px_20px_rgba(0,0,0,0.08)]"
    >
      {/* Merge of main's 16/9 ratio with this branch's CardImage fallback. */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-surface">
        <CardImage
          src={articleImageUrl(article.coverImage)}
          alt={article.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 400px"
          className="object-cover transition group-hover:scale-[1.02]"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#EF2BA0]">
          {article.topicLabel ?? article.topic}
        </span>
        <h3 className="line-clamp-2 text-lg font-bold leading-tight text-[#120F30] group-hover:text-[#0094FF]">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="line-clamp-2 text-sm text-[#5A5A5A]">{article.excerpt}</p>
        )}
        <span className="mt-auto text-xs text-gray-500">
          {fmtDate(article.publishedAt)}
        </span>
      </div>
    </Link>
  );
}
